import MailThreadItem from '@/components/ProjectDetails/Cards/Items/MailThreadItem';
import SummaryCard from '@/components/ProjectDetails/Cards/SummaryCard';
import PERMISSIONS from '@/constants/permissions';
import { fetchLeadMailThreads, fetchMyMailboxRequest } from '@/services/api/email';
import { MailThread } from '@/typings/models/Email';
import { useHasAccess, useHasActiveIntegration } from '@/utils/hooks';
import { INTEGRATION_TYPE_OFFICE365_MAIL } from '@/utils/integrations';
import { F } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { List } from 'antd';
import React, { useEffect, useState } from 'react';

/** How long a just-sent message needs before Graph will return it in a search. */
const SEND_SETTLE_MS = 2500;

export interface MailCardProps {
  leadId: string;
  onShowAll?: (thread?: MailThread) => void;
  /** Opens the composer on its own, without the thread drawer behind it. */
  onCompose?: () => void;
  /** Bumped by the parent after a send, to refetch without a page reload. */
  refreshToken?: number;
}

/**
 * Outlook mail summary for the lead — threads come live from Microsoft Graph
 * (office365_mail integration), nothing is stored locally. Renders nothing at
 * all when the integration is not connected, so partners without it never see
 * an empty card.
 */
const MailCard: React.FC<MailCardProps> = ({ leadId, onShowAll, onCompose, refreshToken }) => {
  const [threads, setThreads] = useState<MailThread[]>([]);
  const [available, setAvailable] = useState(false);
  // Composing needs a Microsoft identity as well as the permission — without a
  // mailbox the send would be refused, so the button is not offered. Checked
  // here rather than assumed, so the + never opens onto a dead end.
  const [canSend, setCanSend] = useState(false);
  const canCompose = useHasAccess([PERMISSIONS.MessageThreads.COMPOSE]);
  // Checked before fetching rather than relying on the 403: without this the
  // card fires a request it knows will be refused on every lead open.
  const canViewMail = useHasAccess([
    PERMISSIONS.MessageThreads.VIEW,
    PERMISSIONS.MessageThreads.VIEW_ALL,
  ]);
  // Same reasoning for the integration: partners without it answer 409 to every
  // thread fetch, so the permission alone would still mean one failed request
  // per lead open. The permission is granted far more widely than the app is
  // installed.
  const hasMailIntegration = useHasActiveIntegration(
    INTEGRATION_TYPE_OFFICE365_MAIL,
  );
  const canFetch = canViewMail && hasMailIntegration;

  useEffect(() => {
    if (!canFetch) return undefined;
    const afterSend = (refreshToken ?? 0) > 0;

    const fetchThreads = () =>
      // A post-send refetch bypasses the cache: the server holds the sweep for
      // three minutes, so the message the agent just sent would otherwise be
      // missing from the card until it expires — which reads as a failed send.
      fetchLeadMailThreads(leadId, afterSend).then((APIResponse: any) => {
        processAPIResponseNow(
          APIResponse,
          {
            onSuccess(response: any) {
              setThreads(response.threads || []);
              setAvailable(true);
            },
            // 409 = integration not connected, 404 = lead without email — hide.
            onError() {
              setAvailable(false);
            },
          },
          'data',
        );
      });

    // Graph does not make a just-sent message searchable the instant the send
    // returns, so refetching immediately tends to come back without it. The
    // wait is the difference between the card showing the new thread and the
    // agent wondering whether the send worked.
    if (!afterSend) {
      fetchThreads();
      return undefined;
    }
    const timer = setTimeout(fetchThreads, SEND_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [leadId, canFetch, refreshToken]);

  useEffect(() => {
    if (!canCompose || !available) return;
    fetchMyMailboxRequest().then((Response: any) => {
      processAPIResponseNow(
        Response,
        {
          onSuccess() {
            setCanSend(true);
          },
          onError() {
            setCanSend(false);
          },
        },
        'data',
      );
    });
  }, [canCompose, available]);

  if (!canFetch || !available) return null;

  return (
    <SummaryCard
      title={<F id="email.card.title" defaultMessage="Emails (Outlook)" />}
      count={threads.length}
      // Without this the card is a dead end whenever the lead has no mail yet:
      // "Show all" only appears once there is something to show, so an agent
      // who could write the first message had nowhere to start.
      onAdd={canSend && onCompose ? () => onCompose() : undefined}
      onShowAll={() => onShowAll?.()}
    >
      <List
        dataSource={threads.slice(0, 2)}
        rowKey={(thread) => `${thread.mailbox_id}|${thread.conversation_id}`}
        renderItem={(thread) => (
          <MailThreadItem thread={thread} onClick={() => onShowAll?.(thread)} />
        )}
      />
    </SummaryCard>
  );
};

export default MailCard;
